import { HttpParams } from '@angular/common/http'
import { Component, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { DataType } from '@grapecity/wijmo'
import { WjFlexGrid, WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { AllowSorting, CellEditEndingEventArgs, Column, DataMap, FlexGrid } from '@grapecity/wijmo.grid'
import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap'

import { BaseComponent, FeatureType, Grid, ListItem, ViewportService, VpUser } from '../../shared'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'

export interface EquipmentSubCategoryPriorityRow {
    categoryCode: string
    description: string
    priority: number | null
}

export interface EquipmentSkillCategoryRow {
    id: string
    code: string
    name: string
    sortOrder: number
    isActive: boolean
}

export interface EquipmentTechRoleRow {
    id: string
    code: string
    name: string
    sortOrder: number
    isActive: boolean
}

export interface EquipmentTechRoleCapabilityMatrix {
    skillCategories: EquipmentSkillCategoryRow[]
    techRoles: EquipmentTechRoleRow[]
    enabledKeys: string[]
}

export interface EquipmentTechRoleAssignmentRow {
    id?: string
    mechanicHrRef: number
    mechanicName: string
    techRoleId: string
    techRoleCode?: string
    techRoleName?: string
}

/** One row per skill category; tech role columns use role.code as property keys. */
export interface CapabilityMatrixRow {
    skillId: string
    skillName: string
    [roleCode: string]: boolean | string
}

@Component({
    templateUrl: './sub-category-priority.component.html',
    styleUrls: ['./sub-category-priority.component.css'],
    standalone: true,
    imports: [
        FormsModule,
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        ActionToolbarCount,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjGridModule,
        WjGridFilterModule,
        WjInputModule,
        AutoSizeDirective,
    ],
})
export class SubCategoryPriorityComponent extends BaseComponent {
    activeTab = 0

    grid: Grid<EquipmentSubCategoryPriorityRow>
    priorityDataMap!: DataMap

    skillCategoryGrid: Grid<EquipmentSkillCategoryRow>
    capabilityGrid: Grid<CapabilityMatrixRow>
    activeDataMap!: DataMap

    skillCategories: EquipmentSkillCategoryRow[] = []
    techRoles: EquipmentTechRoleRow[] = []
    capabilityKeys = new Set<string>()
    capabilityDirty = false

    assignmentRows: EquipmentTechRoleAssignmentRow[] = []
    vistaUsers: VpUser[] = []
    techRoleOptions: ListItem<string>[] = []
    /** Stable reference for wj-combo-box; rebuilt when assignments change (not on every CD cycle). */
    mechanicUserOptions: ListItem<number>[] = []

    newAssignmentHrRef: number | null = null
    newAssignmentRoleId = ''

    private viewport = inject(ViewportService)

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.EquipmentSubCategoryPriorityEdit])
    }

    constructor() {
        super()
        const canEditGrid = this.canEdit
        this.grid = new Grid<EquipmentSubCategoryPriorityRow>(this.services, 'categoryCode', this)
            .wrapColumnHeaders()
            .isReadOnly(!canEditGrid)
            .filterBy(item => [item.categoryCode, item.description])

        this.skillCategoryGrid = new Grid<EquipmentSkillCategoryRow>(this.services, 'id', this)
            .wrapColumnHeaders()
            .isReadOnly(!canEditGrid)
            .filterBy(item => [item.code, item.name])

        this.capabilityGrid = new Grid<CapabilityMatrixRow>(this.services, 'skillId', this)
            .wrapColumnHeaders()
            .isReadOnly(!canEditGrid)
            .allowSorting(AllowSorting.None)

        const priorityItems = [
            new ListItem<number | null>(null, ''),
            ...[1, 2, 3, 4, 5].map(n => new ListItem<number | null>(n, String(n))),
        ]
        this.priorityDataMap = new DataMap(priorityItems, 'value', 'display')

        const activeItems = [new ListItem(true, 'Yes'), new ListItem(false, 'No')]
        this.activeDataMap = new DataMap(activeItems, 'value', 'display')

        this.refresh()
    }

    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.get<EquipmentSubCategoryPriorityRow[]>('api/EquipmentSubCategoryPriority'),
                    this.services.data.get<EquipmentSkillCategoryRow[]>('api/EquipmentSkillCategory'),
                    this.services.data.get<EquipmentTechRoleCapabilityMatrix>('api/EquipmentTechRoleCapability'),
                    this.services.data.get<EquipmentTechRoleAssignmentRow[]>('api/EquipmentTechRoleAssignment'),
                    this.services.data.get<VpUser[]>('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', 'true')),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        this.grid.items = result[0]
                        this.skillCategoryGrid.items = result[1]
                        const matrix = result[2]
                        this.skillCategories = matrix.skillCategories ?? []
                        this.techRoles = matrix.techRoles ?? []
                        this.capabilityKeys = new Set(matrix.enabledKeys ?? [])
                        this.capabilityDirty = false
                        this.assignmentRows = result[3] ?? []
                        this.vistaUsers = (result[4] ?? []).orderBy(x => x.name)
                        this.techRoleOptions = this.techRoles.map(r => new ListItem(r.id, r.name))
                        this.rebuildMechanicUserOptions()
                        this.rebuildCapabilityGrid()
                    },
                }),
        )
    }

    onCapabilityGridInitialized(flexGrid: WjFlexGrid) {
        this.capabilityGrid.initialize(flexGrid)
        flexGrid.frozenColumns = this.viewport.isMobile() ? 0 : 1
        this.configureCapabilityColumns()
        this.rebuildCapabilityGridItems()
    }

    private configureCapabilityColumns() {
        const flex = this.capabilityGrid.flexGrid
        if (!flex) return

        flex.columns.clear()
        flex.columns.push(
            new Column({
                binding: 'skillName',
                header: 'Skill category',
                width: 220,
                isReadOnly: true,
            }),
        )

        for (const role of this.techRoles) {
            flex.columns.push(
                new Column({
                    binding: role.code,
                    header: role.name,
                    width: 88,
                    dataType: DataType.Boolean,
                }),
            )
        }
    }

    private rebuildCapabilityGridItems() {
        const rows: CapabilityMatrixRow[] = this.skillCategories.map(skill => {
            const row: CapabilityMatrixRow = {
                skillId: skill.id,
                skillName: skill.name,
            }
            for (const role of this.techRoles) {
                row[role.code] = this.isCapabilityEnabled(skill.id, role.id)
            }
            return row
        })
        this.capabilityGrid.items = rows
    }

    private rebuildCapabilityGrid() {
        if (this.capabilityGrid.isInitialized) {
            this.configureCapabilityColumns()
        }
        this.rebuildCapabilityGridItems()
    }

    private syncCapabilityKeysFromGrid() {
        this.capabilityKeys.clear()
        for (const row of this.capabilityGrid.items) {
            for (const role of this.techRoles) {
                if (row[role.code] === true) {
                    this.capabilityKeys.add(this.capabilityKey(row.skillId, role.id))
                }
            }
        }
    }

    capabilityCellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel || !this.canEdit) return
        this.syncCapabilityKeysFromGrid()
        this.capabilityDirty = true
    }

    private rebuildMechanicUserOptions() {
        const assigned = new Set(this.assignmentRows.map(x => x.mechanicHrRef))
        this.mechanicUserOptions = this.vistaUsers
            .filter(x => !assigned.has(x.id))
            .map(x => new ListItem(x.id, x.name))
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel || !this.canEdit) return
        const col = flexGrid.columns[args.col]
        if (col.binding !== 'priority') return
        this.updateSubCategoryRecords([flexGrid.rows[args.row].dataItem as EquipmentSubCategoryPriorityRow])
    }

    skillCellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel || !this.canEdit) return
        this.saveSkillCategories()
    }

    private updateSubCategoryRecords(items: EquipmentSubCategoryPriorityRow[]) {
        const body = items.map(r => ({
            categoryCode: r.categoryCode,
            description: r.description,
            priority: r.priority,
        }))
        this.services.data.put('api/EquipmentSubCategoryPriority', body).subscribe({
            error: () => {
                this.services.notification.error('The priority change was not saved')
                this.reloadSubCategoryGrid()
            },
        })
    }

    private reloadSubCategoryGrid() {
        this.services.data.get<EquipmentSubCategoryPriorityRow[]>('api/EquipmentSubCategoryPriority').subscribe({
            next: rows => (this.grid.items = rows),
        })
    }

    addSkillCategory() {
        if (!this.canEdit) return
        const maxSort = this.skillCategoryGrid.items.reduce((m, r) => Math.max(m, r.sortOrder ?? 0), 0)
        this.skillCategoryGrid.items = [
            ...this.skillCategoryGrid.items,
            {
                id: '00000000-0000-0000-0000-000000000000',
                code: '',
                name: '',
                sortOrder: maxSort + 1,
                isActive: true,
            },
        ]
    }

    saveSkillCategories() {
        if (!this.canEdit) return
        const body = this.skillCategoryGrid.items.map(r => ({
            id: r.id,
            code: r.code,
            name: r.name,
            sortOrder: r.sortOrder,
            isActive: r.isActive,
        }))
        this.services.data.put('api/EquipmentSkillCategory', body).subscribe({
            next: () => {
                this.services.notification.success('Skill categories saved')
                this.refresh()
            },
            error: () => this.services.notification.error('Skill categories were not saved'),
        })
    }

    capabilityKey(skillId: string, roleId: string) {
        return `${skillId}|${roleId}`
    }

    isCapabilityEnabled(skillId: string, roleId: string) {
        return this.capabilityKeys.has(this.capabilityKey(skillId, roleId))
    }

    saveCapabilities() {
        if (!this.canEdit) return
        this.syncCapabilityKeysFromGrid()
        const body = { enabledKeys: Array.from(this.capabilityKeys) }
        this.services.data.put('api/EquipmentTechRoleCapability', body).subscribe({
            next: () => {
                this.services.notification.success('Tech role capabilities saved')
                this.capabilityDirty = false
            },
            error: () => this.services.notification.error('Capabilities were not saved'),
        })
    }

    addAssignment() {
        if (!this.canEdit || !this.newAssignmentHrRef || !this.newAssignmentRoleId) return
        const user = this.vistaUsers.find(x => x.id === this.newAssignmentHrRef)
        const role = this.techRoles.find(x => x.id === this.newAssignmentRoleId)
        if (!user || !role) return
        if (this.assignmentRows.some(x => x.mechanicHrRef === user.id)) {
            this.services.notification.error('That mechanic is already assigned')
            return
        }
        this.assignmentRows = [
            ...this.assignmentRows,
            {
                mechanicHrRef: user.id,
                mechanicName: user.name,
                techRoleId: role.id,
                techRoleCode: role.code,
                techRoleName: role.name,
            },
        ]
        this.newAssignmentHrRef = null
        this.newAssignmentRoleId = ''
        this.rebuildMechanicUserOptions()
        this.saveAssignments()
    }

    removeAssignment(row: EquipmentTechRoleAssignmentRow) {
        if (!this.canEdit) return
        this.assignmentRows = this.assignmentRows.filter(x => x.mechanicHrRef !== row.mechanicHrRef)
        this.rebuildMechanicUserOptions()
        this.saveAssignments()
    }

    onAssignmentRoleChange(row: EquipmentTechRoleAssignmentRow) {
        if (!this.canEdit) return
        const role = this.techRoles.find(x => x.id === row.techRoleId)
        if (role) {
            row.techRoleCode = role.code
            row.techRoleName = role.name
        }
        this.saveAssignments()
    }

    private saveAssignments() {
        const body = this.assignmentRows.map(r => ({
            id: r.id,
            mechanicHrRef: r.mechanicHrRef,
            mechanicName: r.mechanicName,
            techRoleId: r.techRoleId,
        }))
        this.services.data.put('api/EquipmentTechRoleAssignment', body).subscribe({
            error: () => {
                this.services.notification.error('Mechanic assignments were not saved')
                this.refresh()
            },
        })
    }

}
